import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { supabase } from "../../lib/supabase";
import { useV2Session } from "../context/V2SessionContext";
import { getSafeAuthMessage } from "../lib/v2AuthUtils";

export default function V2ResetPassword() {
  const navigate = useNavigate();
  const { authSession, isPasswordRecovery, loading } = useV2Session();
  const mountedRef = useRef(true);
  const processedRecoveryRef = useRef(false);
  const [recoverySession, setRecoverySession] = useState(null);
  const [checkingRecovery, setCheckingRecovery] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (loading) return undefined;
    if (processedRecoveryRef.current) return undefined;
    processedRecoveryRef.current = true;

    async function establishRecoverySession() {
      const url = new URL(window.location.href);
      const callback = readRecoveryCallback(url);

      try {
        let result;

        if (callback.tokenHash) {
          if (!isRecoveryType(callback.type)) throw new Error("Invalid recovery callback.");
          result = await supabase.auth.verifyOtp({ token_hash: callback.tokenHash, type: "recovery" });
        } else if (callback.code) {
          if (!isRecoveryType(callback.type)) throw new Error("Invalid recovery callback.");
          result = await supabase.auth.exchangeCodeForSession(callback.code);
        } else if (callback.accessToken && callback.refreshToken) {
          if (!isRecoveryType(callback.type)) throw new Error("Invalid recovery callback.");
          result = await supabase.auth.setSession({
            access_token: callback.accessToken,
            refresh_token: callback.refreshToken,
          });
        } else if (isPasswordRecovery && authSession) {
          result = { data: { session: authSession }, error: null };
        } else if (isRecoveryType(callback.type)) {
          result = await supabase.auth.getSession();
        } else {
          result = { data: { session: null }, error: new Error("No recovery session.") };
        }

        const session = result?.data?.session || null;
        if (result?.error || !isValidRecoverySession(session)) {
          throw result?.error || new Error("Invalid recovery session.");
        }

        if (mountedRef.current) setRecoverySession(session);
      } catch (_error) {
        if (mountedRef.current) setRecoverySession(null);
      } finally {
        scrubRecoveryCallback(url, callback);
        if (mountedRef.current) setCheckingRecovery(false);
      }
    }

    establishRecoverySession();
    return undefined;
  }, [authSession, isPasswordRecovery, loading]);

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setError("");

    if (!recoverySession) {
      setError("Invalid or expired link.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    const result = await supabase.auth.updateUser({ password });
    setSubmitting(false);

    const updatedUser = result.data?.user || result.data?.session?.user || null;
    if (result.error || !updatedUser?.id || updatedUser.id !== recoverySession.user.id) {
      setError(getSafeAuthMessage(result.error, "Password update failed."));
      return;
    }

    setSuccess(true);
    await supabase.auth.signOut();
    window.setTimeout(() => navigate("/login", { replace: true }), 800);
  };

  if (loading || checkingRecovery) {
    return <AuthShell title="Reset password" notes="Checking your recovery link..." />;
  }

  if (!recoverySession) {
    return (
      <AuthShell title="Invalid or expired link" notes="Request a fresh reset link before changing your password.">
        <Link to="/forgot-password">
          <Button type="button">Request reset link</Button>
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={success ? "Password updated" : "Reset password"} notes="Choose a new password for your account.">
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="v2-reset-password">New password</Label>
          <Input
            id="v2-reset-password"
            type="password"
            autoComplete="new-password"
            minLength={6}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              setError("");
            }}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="v2-reset-confirm">Confirm password</Label>
          <Input
            id="v2-reset-confirm"
            type="password"
            autoComplete="new-password"
            minLength={6}
            value={confirmPassword}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
              setError("");
            }}
            required
          />
        </div>
        {error && <p className="text-sm font-medium text-destructive">{error}</p>}
        {success && <p className="text-sm font-medium text-emerald-700">Password updated. Please sign in again.</p>}
        <Button type="submit" disabled={submitting || success}>
          {submitting ? "Updating password..." : "Update password"}
        </Button>
      </form>
    </AuthShell>
  );
}

function readRecoveryCallback(url) {
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  return {
    code: url.searchParams.get("code"),
    tokenHash: url.searchParams.get("token_hash") || hashParams.get("token_hash"),
    type: url.searchParams.get("type") || hashParams.get("type"),
    accessToken: url.searchParams.get("access_token") || hashParams.get("access_token"),
    refreshToken: url.searchParams.get("refresh_token") || hashParams.get("refresh_token"),
    hasAuthHash: hasAuthCredentials(hashParams),
  };
}

function isRecoveryType(type) {
  const normalizedType = String(type || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  return normalizedType === "recovery" || normalizedType === "password_recovery";
}

function isValidRecoverySession(session) {
  return Boolean(session?.access_token && session?.refresh_token && session?.user?.id);
}

function scrubRecoveryCallback(url, callback) {
  const credentialParams = [
    "access_token",
    "code",
    "expires_at",
    "expires_in",
    "provider_refresh_token",
    "provider_token",
    "refresh_token",
    "token",
    "token_hash",
    "token_type",
    "type",
  ];
  const hasAuthQuery = credentialParams.some((key) => url.searchParams.has(key));
  if (!hasAuthQuery && !callback.hasAuthHash) return;

  credentialParams.forEach((key) => url.searchParams.delete(key));
  if (callback.hasAuthHash) url.hash = "";
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
}

function hasAuthCredentials(params) {
  return ["access_token", "refresh_token", "token", "token_hash", "type"].some((key) => params.has(key));
}

function AuthShell({ title, notes, children }) {
  return (
    <main className="min-h-screen bg-background px-4 py-12 text-foreground">
      <section className="mx-auto max-w-xl rounded-lg border border-border bg-card p-6">
        <h1 className="text-3xl font-extrabold">{title}</h1>
        <p className="mt-3 text-muted-foreground">{notes}</p>
        {children && <div className="mt-4">{children}</div>}
      </section>
    </main>
  );
}
